import { track } from '@amplitude/analytics-browser';
import { autoAdjustHiringStatusToolTip } from '@features/employerTeam/constants/constants';
import ConfirmationModal from '@features/enterprise/components/ConfirmationModal';
import { useEditTemplate } from '@features/enterprise/hooks/useEditTemplate';
import { EnterpriseTemplate } from '@features/enterprise/types';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';
import { HelpOutline } from '@mui/icons-material';
import {
  Box,
  Switch,
  Typography,
  TextField,
  Tooltip,
  Stack,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { white } from '@styles/colors';
import { bold, medium } from '@styles/fontWeights';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { getCookie } from '@utils/getCookie';
import { selectedPlanName } from '@utils/pricing';
import React, { useContext, useState, useCallback, useEffect } from 'react';

import AutoApplicationsAction from './AutoApplicationsAction';
import AutoApplicationSelections from './AutoApplicationSelections';

type HiringAutomationProps = {
  automationData?: EnterpriseTemplate;
};
type Setting = {
  label: string;
  event: string;
  customEvent?: string;
  customInputEvent?: string;
  value: string | number | boolean;
  field: string;
  action: string;
  min: number;
  max: number;
  showDropDown: boolean;
  tooltip: string;
  reminder?: {
    label: string;
    event: string;
    value: number;
    field: string;
    tooltip: string;
    min: number;
    max: number;
  };
  disable?: boolean;
};

const HiringAutomation: React.FC<HiringAutomationProps> = ({
  automationData,
}) => {
  const dontShowWarningPopup = getCookie('dontShowWarningPopup');
  const isAppliedChanges =
    automationData?.apply_changes && !dontShowWarningPopup;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const {
    authData: { user },
  } = useContext(authContext) as {
    authData: {
      user: {
        company?: {
          id: number;
          keep_declined_applications?: number;
          keep_unattended_applications?: number;
          message_reminder_days?: number;
          unresponsive_message?: number;
          unresponsive_interview?: number;
          auto_save_applications?: boolean;
          interview_reminder_days?: number;
          auto_interview_invite?: boolean;
          auto_job_board_refresh?: number;
          plan: number;
          check_list: {
            auto_adjust_hiring_status?: boolean;
          };
          additional_info?: {
            hire_flix_location: string;
          };
          hire_flix_plan?: {
            auto_refill: boolean;
            status: string;
          };
        };
      } | null;
    };
  };

  const isCompany = user?.company;

  const isHireflixLocation =
    isCompany?.additional_info?.hire_flix_location === 'true';
  const isHireflixAutoRefill = isCompany?.hire_flix_plan?.auto_refill;
  const isHireflixPaid = isCompany?.hire_flix_plan?.status === 'Paid';

  const isAutoFillEnabled = isHireflixLocation && isHireflixAutoRefill;

  const selectedPlan = selectedPlanName(user?.company?.plan || 0);
  const { updateProfile } = useUpdateProfile({
    setErrorText,
  });

  const { editTemplate, isUpdatingTemplate } = useEditTemplate({
    setErrorText,
  });

  useEffect(() => {
    if (isAppliedChanges) {
      setIsConfirmOpen(true);
    }
  }, []);

  const settings: Setting[] = [
    ...(isCompany
      ? [
          {
            label: 'Teams Auto Adjust Hiring Status',
            event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_TOGGLE_TEAM}-${selectedPlan}`,
            value:
              automationData?.auto_adjust_hiring_status ??
              user?.company?.check_list?.auto_adjust_hiring_status ??
              false,
            field: 'auto_adjust_hiring_status',
            action: 'Enable',
            min: 0,
            max: 120,
            showDropDown: false,
            tooltip: autoAdjustHiringStatusToolTip,
          },
        ]
      : []),
    {
      label: 'Declined Applications',
      event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_DECLINED}-${selectedPlan}`,
      customEvent: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_DROPDOWN_CUSTOM}-${selectedPlan}`,
      customInputEvent: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_CUSTOM}-${selectedPlan}`,
      value:
        automationData?.keep_declined_applications ??
        user?.company?.keep_declined_applications ??
        0,
      field: 'keep_declined_applications',
      action: 'Archive After',
      min: 0,
      max: 120,
      showDropDown: false,
      tooltip:
        'Stay organized - Archived application history is always available and can be searched in your archive folder.',
    },
    {
      label: 'Unattended Applications',
      customEvent: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_DROPDOWN_CUSTOM}-${selectedPlan}`,
      customInputEvent: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_CUSTOM}-${selectedPlan}`,
      event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_UNATTENDED}-${selectedPlan}`,
      value:
        automationData?.keep_unattended_applications ??
        user?.company?.keep_unattended_applications ??
        0,
      field: 'keep_unattended_applications',
      action: 'Decline after',
      min: 0,
      showDropDown: false,
      max: 120,
      tooltip:
        "Keep your talent pool fresh - We'll automatically decline older unattended applications for you, and let the applicants know they can reapply if they're still interested.",
    },
    {
      label: 'Messaged, No Response',
      customEvent: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_DROPDOWN_CUSTOM}-${selectedPlan}`,
      customInputEvent: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_CUSTOM}-${selectedPlan}`,
      event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_NO_RESPONSE}-${selectedPlan}`,
      value:
        automationData?.unresponsive_message ??
        user?.company?.unresponsive_message ??
        0,
      field: 'unresponsive_message',
      action: 'Decline after',
      min: 0,
      showDropDown: false,
      max: 120,
      reminder: {
        label: 'Send reminders every 24 hours for:',
        event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_REMINDER_MESSAGE}-${selectedPlan}`,
        value:
          automationData?.message_reminder_days ??
          user?.company?.message_reminder_days ??
          0,
        field: 'message_reminder_days',
        tooltip:
          'Stay organized - Archived application history is always available and can be searched in your archive folder.',
        min: 0,
        max: 7,
      },
      tooltip:
        "Keep your talent pool fresh - We'll automatically decline older applications that you have messaged but never received a response from, and let the applicants know they can reapply if they're still interested",
    },
    {
      label: 'Interview Requested, No Response',
      customEvent: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_DROPDOWN_CUSTOM}-${selectedPlan}`,
      customInputEvent: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_CUSTOM}-${selectedPlan}`,
      event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_REMINDER_MESSAGE}-${selectedPlan}`,
      value:
        automationData?.unresponsive_interview ??
        user?.company?.unresponsive_interview ??
        0,
      field: 'unresponsive_interview',
      action: 'Decline after',
      min: 0,
      showDropDown: false,
      max: 120,
      reminder: {
        label: 'Send reminders every 24 hours for:',
        event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_REMINDER_INTERVIEW}-${selectedPlan}`,
        value:
          automationData?.interview_reminder_days ??
          user?.company?.interview_reminder_days ??
          0,
        field: 'interview_reminder_days',
        tooltip:
          'Ensure follow-up by setting reminders for interview requests that have not been responded to.',
        min: 0,
        max: 7,
      },
      tooltip:
        "Keep your talent pool fresh - We'll automatically decline older applications that you have invited to interview but never received a response from, and let the applicants know they can reapply if they're still interested.",
    },
    {
      label: 'Auto Save Applications',
      event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_TOGGLE_SAVE}-${selectedPlan}`,
      value:
        automationData?.auto_save_applications ??
        user?.company?.auto_save_applications ??
        false,
      field: 'auto_save_applications',
      action: 'Enable',
      min: 0,
      showDropDown: false,
      max: 120,
      tooltip:
        'This feature requires the use of prequalifying questions, and a position status to be set to accepting. When prequalifying questions are all answered correctly for jobs set to accepting, new applications will automatically be saved.',
    },
    {
      label: 'Auto Invite to Interview',
      event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_TOGGLE_INVITE}-${selectedPlan}`,
      value:
        automationData?.auto_interview_invite ??
        user?.company?.auto_interview_invite ??
        false,
      field: 'auto_interview_invite',
      action: 'Enable',
      min: 0,
      max: 120,
      showDropDown: false,
      disable: isHireflixPaid && !isAutoFillEnabled,
      tooltip:
        'This feature requires the use of prequalifying questions, future interview availability, and the position status to be set to hiring. When prequalifying questions are all answered correctly for jobs set to hiring, new applications will automatically be invited to interview if there are future interview blocks available on the calendar.',
    },
    {
      label: 'Auto Job Board Refresh',
      event: `${amplitudeEventsName.JOBMANAGEMENT_CLICK_FIELD_DROPDOWN_JOBBOARD}-${selectedPlan}`,
      value:
        automationData?.auto_job_board_refresh ??
        user?.company?.auto_job_board_refresh ??
        0,
      field: 'auto_job_board_refresh',
      action: 'Refresh Every',
      min: 0,
      max: 120,
      showDropDown: true,
      tooltip:
        'StaffedUp will automatically repost new jobs on your behalf to all external job boards to increase reach and drive additional applicant traffic.',
    },
  ];
  const simpleMenuItems = {
    '2 Weeks': 14,
    '1 Month': 30,
    '2 Months': 60,
    '3 Months': 90,
  };

  const simpleItems = {
    '30': 30,
    '60': 60,
    '90': 90,
    '120': 120,
  };
  const reminderItems = {
    'Do not send reminders': 0,
    '1': 1,
    '2': 2,
    '3': 3,
    '4': 4,
    '5': 5,
    '6': 6,
    '7': 7,
  };

  return (
    <Box
      sx={{
        padding: { xs: automationData ? '0' : '15px 10px' },
        maxWidth: '100%',
        margin: '0 auto',
      }}
    >
      {!automationData && (
        <Stack bgcolor={white} borderRadius={8} p={4}>
          <Typography variant="h4" fontSize="24px" fontWeight="bold">
            Hiring Automation
          </Typography>
        </Stack>
      )}
      <Box
        bgcolor={white}
        padding={3}
        borderRadius={8}
        mt={automationData ? 0 : 3}
      >
        {settings.map((setting, index) => (
          <Box
            key={index}
            sx={{ mt: 3, pb: 2, borderBottom: '1px solid #e0e0e0' }}
          >
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Typography
                fontWeight={medium}
                variant="body1"
                sx={{ maxWidth: 'fit-content' }}
              >
                {setting.label}
              </Typography>
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                flexWrap="wrap"
              >
                {setting.action !== 'Enable' && (
                  <Typography variant="body2" sx={{ color: 'gray' }}>
                    {`${setting.action}:`}
                  </Typography>
                )}
                {setting.action !== 'Enable' && !setting.showDropDown && (
                  <AutoApplicationSelections
                    setting={setting}
                    updateProfile={(data) => {
                      if (automationData) {
                        editTemplate({
                          ...data,
                          template_id: automationData.id,
                        });
                      } else {
                        updateProfile(data);
                      }
                    }}
                    simpleItems={simpleItems}
                    showReminder={false}
                  />
                )}
                {setting.action === 'Enable' && !setting.showDropDown && (
                  <AutoApplicationsAction
                    field={setting.field}
                    value={Boolean(setting.value)}
                    isDisable={setting?.disable}
                    updateProfile={(data) => {
                      isCompany && track(setting.event);
                      if (automationData) {
                        editTemplate({
                          ...data,
                          template_id: automationData.id,
                        });
                      } else {
                        updateProfile(data);
                      }
                    }}
                  />
                )}
                {setting.showDropDown && (
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      onClick={() => {
                        isCompany && track(setting.event);
                      }}
                      onChange={(e) => {
                        automationData
                          ? editTemplate({
                              [setting.reminder
                                ? setting.reminder.field
                                : setting.field]:
                                e.target.value == 'Never' ? 0 : e.target.value,
                              template_id: automationData?.id,
                            })
                          : updateProfile({
                              [setting.reminder
                                ? setting.reminder.field
                                : setting.field]:
                                e.target.value == 'Never' ? 0 : e.target.value,
                            });
                      }}
                      defaultValue={
                        setting.reminder
                          ? setting.reminder.value
                          : setting.value
                      }
                    >
                      {Object.entries(
                        setting.field === 'auto_job_board_refresh'
                          ? simpleMenuItems
                          : simpleItems,
                      ).map(([label, value]) => (
                        <MenuItem key={label} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {setting.action !== 'Enable' && !setting.showDropDown && (
                  <Typography variant="body2" sx={{ color: 'gray' }}>
                    Days
                  </Typography>
                )}
                <Tooltip
                  title={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: setting.reminder
                          ? setting.reminder.tooltip
                          : setting.tooltip,
                      }}
                    />
                  }
                  placement="top"
                  arrow
                >
                  <HelpOutline color="secondary" fontSize="small" />
                </Tooltip>
              </Stack>
            </Stack>
            {setting.reminder && (
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'gray', flex: '1 1 auto' }}
                  fontWeight={bold}
                >
                  {setting.reminder.label}
                </Typography>
                <AutoApplicationSelections
                  setting={setting}
                  updateProfile={(data) => {
                    if (automationData) {
                      editTemplate({
                        ...data,
                        template_id: automationData.id,
                      });
                    } else {
                      updateProfile(data);
                    }
                  }}
                  simpleItems={simpleItems}
                  reminderItems={reminderItems}
                  showReminder
                />
                {setting.action !== 'Enable' && !setting.showDropDown && (
                  <Typography variant="body2" sx={{ color: 'gray' }}>
                    Days
                  </Typography>
                )}
                <Tooltip title={setting.reminder.tooltip} placement="top" arrow>
                  <HelpOutline color="secondary" fontSize="small" />
                </Tooltip>
              </Stack>
            )}
          </Box>
        ))}
      </Box>
      <ConfirmationModal
        open={isConfirmOpen}
        onConfirm={() => setIsConfirmOpen(false)}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </Box>
  );
};

export default HiringAutomation;
